import type { PblRole } from '@/types/pbl';
import type { PblVettingStatus } from '@/types/pbl';

const PBL_ROLES: PblRole[] = [
  'pbl_admin',
  'pbl_vetting_reviewer',
  'oc_admin',
  'oc_staff',
  'pbl_lawyer',
  'citizen',
];

const getStoredPblRole = () => {
  if (typeof window === 'undefined') return undefined;

  try {
    return window.localStorage.getItem('pblRole') || window.localStorage.getItem('pbl_role') || undefined;
  } catch {
    return undefined;
  }
};

const normalizeRole = (role: unknown): PblRole | undefined => {
  if (typeof role !== 'string') return undefined;

  return PBL_ROLES.find((pblRole) => pblRole === role);
};

const isActivePblRole = (roleItem: any) =>
  roleItem?.is_active === true || roleItem?.is_active === 1 || roleItem?.is_active === '1';

export const getUserPblRoles = (userData?: any): PblRole[] => {
  const roleList = userData?.pbl_roles;

  if (!Array.isArray(roleList)) {
    const storedRole = normalizeRole(getStoredPblRole());
    return storedRole ? [storedRole] : [];
  }

  return roleList.reduce<PblRole[]>((roles, roleItem) => {
    const normalized = normalizeRole(typeof roleItem === 'string' ? roleItem : roleItem?.role);

    if (!normalized) return roles;
    if (typeof roleItem !== 'string' && !isActivePblRole(roleItem)) return roles;

    return roles.includes(normalized) ? roles : [...roles, normalized];
  }, []);
};

export const hasPblRole = (userData: any, role: PblRole) => getUserPblRoles(userData).includes(role);

export const getUserPblRole = (userData?: any): PblRole | undefined => {
  return getUserPblRoles(userData)[0];
};

export const isPblUser = (userData?: any) => Boolean(getUserPblRole(userData));

export const isPblVettingReviewer = (userData?: any) => hasPblRole(userData, 'pbl_vetting_reviewer');

export const isPblAdmin = (userData?: any) => hasPblRole(userData, 'pbl_admin');

export const isPblLawyer = (userData?: any) => hasPblRole(userData, 'pbl_lawyer');

export const isPblCitizen = (userData?: any) => hasPblRole(userData, 'citizen');

export const isPblOcAdminOrStaff = (userData?: any) =>
  hasPblRole(userData, 'oc_admin') || hasPblRole(userData, 'oc_staff');

export const getPblLawyerVettingStatus = (userData?: any): PblVettingStatus | undefined => {
  const status = userData?.pbl_lawyer_profile?.vetting_status;

  if (
    status === 'pending_review' ||
    status === 'approved' ||
    status === 'resubmit_required' ||
    status === 'rejected' ||
    status === 'inactive' ||
    status === 'withdrawn'
  ) {
    return status;
  }

  return undefined;
};
